import {useState, useEffect, Fragment} from "react";
import {Listbox, Transition} from "@headlessui/react";
import { ChevronUpDownIcon, CheckIcon } from '@heroicons/react/20/solid';

export const CampusLockUp = ({onUpdate, active, isViewOnly = false, initialData}) => {

    const [campusLockUpForm, setCampusLockUpForm] = useState({
        lockUpStaffType: '',
        lockUpStaffName: '',
        lockUpNotes: '',
        lockUpTime: '',
        doorAccessCode: '',
    });

    // Populate form with initial data when provided
    useEffect(() => {
        if (initialData && Object.keys(initialData).length > 0) {
            setCampusLockUpForm(prevState => ({
                ...prevState,
                ...initialData
            }));
        }
    }, [initialData]);

    const [selectedStaffType, setSelectedStaffType] = useState(null);

    const staffTypes = [
        { id: 'janitorial', name: 'Janitorial Staff (before 8:00 PM)' },
        { id: 'school', name: 'School Staff (after 8:00 PM)' },
        { id: 'church', name: 'Church Staff (after 8:00 PM)' },
    ];

    // Update selectedStaffType when form data changes
    useEffect(() => {
        if (campusLockUpForm.lockUpStaffType) {
            const matchingType = staffTypes.find(type => type.id === campusLockUpForm.lockUpStaffType);
            if (matchingType) {
                setSelectedStaffType(matchingType);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [campusLockUpForm.lockUpStaffType]);

    const handleStaffTypeChange = (value) => {
        setSelectedStaffType(value);
        handleChange({ target: { name: 'lockUpStaffType', value: value.id } });
    };

    const handleChange = (event) => {
        const { name, value } = event.target;
        const newData = { ...campusLockUpForm, [name]: value };
        setCampusLockUpForm(newData);
        onUpdate(newData); // Pass updated data to parent
    };

    if (!active) return null; // Only render if active

    return (
        <div className="rounded-lg">
            <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                <div className="sm:col-span-3">
                    <label htmlFor="lockUpStaffType" className="block text-sm font-medium text-gray-700">Staff Type</label>
                    <div className="mt-1">
                        <Listbox value={selectedStaffType} onChange={handleStaffTypeChange} disabled={isViewOnly}>
                            {({ open }) => (
                                <>
                                    <div className="relative">
                                        <Listbox.Button className="relative w-full bg-white border border-gray-300 rounded-md shadow-sm pl-3 pr-10 py-2 text-left cursor-default focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm">
                                            <span className="block truncate">{selectedStaffType ? selectedStaffType.name : 'Select Staff Type'}</span>
                                            <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                                                <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                                            </span>
                                        </Listbox.Button>

                                        <Transition
                                            show={open}
                                            as={Fragment}
                                            leave="transition ease-in duration-100"
                                            leaveFrom="opacity-100"
                                            leaveTo="opacity-0"
                                        >
                                            <Listbox.Options className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                                                {staffTypes.map((staffType) => (
                                                    <Listbox.Option
                                                        key={staffType.id}
                                                        className={({ active }) =>
                                                            `cursor-default select-none relative py-2 pl-10 pr-4 ${
                                                                active ? 'text-white bg-indigo-600' : 'text-gray-900'
                                                            }`
                                                        }
                                                        value={staffType}
                                                    >
                                                        {({ selected, active }) => (
                                                            <>
                                                                <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                                                    {staffType.name}
                                                                </span>
                                                                {selected ? (
                                                                    <span
                                                                        className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                                                                            active ? 'text-white' : 'text-indigo-600'
                                                                        }`}
                                                                    >
                                                                        <CheckIcon className="w-5 h-5" aria-hidden="true" />
                                                                    </span>
                                                                ) : null}
                                                            </>
                                                        )}
                                                    </Listbox.Option>
                                                ))}
                                            </Listbox.Options>
                                        </Transition>
                                    </div>
                                </>
                            )}
                        </Listbox>
                    </div>
                </div>

                <div className="sm:col-span-3">
                    <label htmlFor="lockUpStaffName" className="block text-sm font-medium text-gray-700">Staff Name</label>
                    <div className="mt-1">
                        <input
                            type="text"
                            name="lockUpStaffName"
                            id="lockUpStaffName"
                            value={campusLockUpForm.lockUpStaffName}
                            onChange={handleChange}
                            readOnly={isViewOnly}
                            disabled={isViewOnly}
                            className={`shadow-sm ${!isViewOnly ? 'focus:ring-indigo-500 focus:border-indigo-500' : ''} block w-full sm:text-sm ${isViewOnly ? 'bg-gray-100' : 'border-gray-300'} rounded-md`}
                            placeholder="Enter staff name"
                        />
                    </div>
                </div>
                
                <div className="sm:col-span-3">
                    <label htmlFor="lockUpTime" className="block text-sm font-medium text-gray-700">Lock-Up Time</label>
                    <div className="mt-1">
                        <input
                            type="time"
                            name="lockUpTime"
                            id="lockUpTime"
                            value={campusLockUpForm.lockUpTime}
                            onChange={handleChange}
                            readOnly={isViewOnly}
                            disabled={isViewOnly}
                            className={`shadow-sm ${!isViewOnly ? 'focus:ring-indigo-500 focus:border-indigo-500' : ''} block w-full sm:text-sm ${isViewOnly ? 'bg-gray-100' : 'border-gray-300'} rounded-md`}
                        />
                    </div>
                </div>
                
                <div className="sm:col-span-3">
                    <label htmlFor="doorAccessCode" className="block text-sm font-medium text-gray-700">Door Access Code (if applicable)</label>
                    <div className="mt-1">
                        <input
                            type="text"
                            name="doorAccessCode"
                            id="doorAccessCode"
                            value={campusLockUpForm.doorAccessCode}
                            onChange={handleChange}
                            readOnly={isViewOnly}
                            disabled={isViewOnly}
                            className={`shadow-sm ${!isViewOnly ? 'focus:ring-indigo-500 focus:border-indigo-500' : ''} block w-full sm:text-sm ${isViewOnly ? 'bg-gray-100' : 'border-gray-300'} rounded-md`}
                            placeholder="Enter access code if needed"
                        />
                    </div>
                </div>
                
                <div className="sm:col-span-6">
                    <label htmlFor="lockUpNotes" className="block text-sm font-medium text-gray-700">Additional Notes</label>
                    <div className="mt-1">
                        <textarea
                            name="lockUpNotes"
                            id="lockUpNotes"
                            rows="3"
                            value={campusLockUpForm.lockUpNotes}
                            onChange={handleChange}
                            readOnly={isViewOnly}
                            disabled={isViewOnly}
                            className={`shadow-sm ${!isViewOnly ? 'focus:ring-indigo-500 focus:border-indigo-500' : ''} block w-full sm:text-sm ${isViewOnly ? 'bg-gray-100' : 'border-gray-300'} rounded-md`}
                            placeholder="Any special instructions for lock-up"
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}